import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { API, api, fmtDate, fmtQty } from "../../api";
import { Printer } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import StatusBadge from "../../components/StatusBadge";
import MfgTabs from "../../components/MfgTabs";

export default function Lots() {
  const [lots, setLots] = useState(null);
  const [search, setSearch] = useState("");
  const [params] = useSearchParams();
  const [qc, setQc] = useState(params.get("qc") || "");
  const [labelOpen, setLabelOpen] = useState(false);
  const [labelSel, setLabelSel] = useState({});
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/lots", { params: { search, qc_status: qc } }).then((r) => setLots(r.data)).catch(() => {});
  }, [search, qc]);

  const selectedIds = Object.keys(labelSel).filter((k) => labelSel[k]);

  return (
    <div data-testid="lots-page" className="space-y-6">
      <h1 className="font-heading text-3xl font-extrabold text-[#002B5D] tracking-tight">Manufacturing</h1>
      <MfgTabs />
      <div className="flex flex-wrap gap-3 mt-4 items-center">
        <input data-testid="lot-search-input" placeholder="Search lot number..." value={search} onChange={(e) => setSearch(e.target.value)} className="sk-input max-w-xs" />
        <select data-testid="lot-qc-filter" className="sk-input max-w-[180px]" value={qc} onChange={(e) => setQc(e.target.value)}>
          <option value="">All QC statuses</option>
          {["Pending", "Passed", "Failed", "Hold"].map((s) => <option key={s}>{s}</option>)}
        </select>
        <button data-testid="lots-print-labels-button" onClick={() => setLabelOpen(true)}
          className="inline-flex items-center gap-2 h-10 px-4 rounded-md font-heading font-bold text-sm text-white ml-auto" style={{ backgroundColor: "#002B5D" }}>
          <Printer className="w-4 h-4" /> Print QR Labels
        </button>
      </div>
      <div className="sk-card overflow-x-auto">
        <table className="w-full sk-table" data-testid="lots-table">
          <thead><tr><th>Lot Number</th><th>Product</th><th>MO</th><th className="text-right">Produced</th><th className="text-right">Deducted</th><th className="text-right">Available</th><th>QC</th><th>Date</th></tr></thead>
          <tbody>
            {!lots && <tr><td colSpan={8} className="text-center text-slate-400">Loading...</td></tr>}
            {lots?.length === 0 && <tr><td colSpan={8} className="text-center text-slate-400">No lots yet — generate one from a Manufacturing Order</td></tr>}
            {lots?.map((l) => (
              <tr key={l.id} className="cursor-pointer" onClick={() => navigate(`/lots/${l.id}`)} data-testid={`lot-row-${l.lot_number}`}>
                <td className="font-mono font-semibold text-[#002B5D]">{l.lot_number}</td>
                <td>{l.product_name}</td>
                <td className="font-mono text-xs">{l.mo_number}</td>
                <td className="text-right font-mono">{fmtQty(l.produced_qty)}</td>
                <td className="text-right font-mono text-rose-600">{fmtQty(l.deducted_qty)}</td>
                <td className="text-right font-mono font-semibold text-green-700">{fmtQty(l.current_qty)} {l.uom}</td>
                <td><StatusBadge status={l.qc?.status || "Pending"} testid={`lot-qc-${l.lot_number}`} /></td>
                <td className="text-slate-500">{fmtDate(l.production_date || l.created_at)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={labelOpen} onOpenChange={setLabelOpen}>
        <DialogContent className="max-w-md" data-testid="lots-labels-dialog">
          <DialogHeader><DialogTitle className="font-heading text-[#002B5D]">Print QR Labels</DialogTitle></DialogHeader>
          <p className="text-xs text-slate-500">Select lots — all their boxes will be printed. Label size 100mm × 44.4mm, 12 labels per A4 sheet.</p>
          <div className="max-h-72 overflow-auto border border-border rounded-md divide-y divide-border">
            {(lots || []).map((l) => (
              <label key={l.id} data-testid={`label-lot-option-${l.lot_number}`} className="flex items-center gap-3 px-4 py-2.5 cursor-pointer hover:bg-slate-50 text-sm">
                <input type="checkbox" className="w-4 h-4" checked={Boolean(labelSel[l.id])}
                  onChange={(e) => setLabelSel((s) => ({ ...s, [l.id]: e.target.checked }))} />
                <span className="font-mono font-semibold text-[#002B5D]">{l.lot_number}</span>
                <span className="text-slate-500">{l.product_name}</span>
              </label>
            ))}
          </div>
          <a data-testid="lots-labels-download" href={`${API}/labels.pdf?lot_ids=${selectedIds.join(",")}`} download
            onClick={() => setLabelOpen(false)}
            className={`w-full h-11 rounded-md font-heading font-bold text-white inline-flex items-center justify-center gap-2 ${selectedIds.length === 0 ? "opacity-40 pointer-events-none" : ""}`}
            style={{ backgroundColor: "#EB1C27" }}>
            <Printer className="w-4 h-4" /> Download Labels PDF ({selectedIds.length} lot{selectedIds.length === 1 ? "" : "s"})
          </a>
        </DialogContent>
      </Dialog>
    </div>
  );
}
